import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    MenuItem,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { DatePicker } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';

import SftGlobalData from '../../library/SftGlobalData';
import ReportResultGrid from './ReportResultGrid';
import SizeMatrixReportGrid, { isSizeMatrix } from './SizeMatrixReportGrid';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const localizedTitle = (report, lang) => {
    if (!report) return '';
    switch (lang) {
        case 'nl': return report.reptitle  || report.reptitle2 || report.reptitle3 || report.reptitle4 || '';
        case 'fr': return report.reptitle2 || report.reptitle  || report.reptitle3 || report.reptitle4 || '';
        case 'en': return report.reptitle3 || report.reptitle  || report.reptitle2 || report.reptitle4 || '';
        default:   return report.reptitle  || '';
    }
};

const localizedVarLabel = (v, lang) => {
    switch (lang) {
        case 'nl': return v.repvar       || v.repvarlang2 || v.repvarlang3 || v.repvar;
        case 'fr': return v.repvarlang2  || v.repvar      || v.repvarlang3 || v.repvar;
        case 'en': return v.repvarlang3  || v.repvar      || v.repvarlang2 || v.repvar;
        default:   return v.repvar;
    }
};

const localizedCodeLabel = (option, lang) => {
    switch (lang) {
        case 'nl': return option.label;
        case 'fr': return option.codeoms_lang2 || option.label;
        case 'en': return option.codeoms_lang3 || option.label;
        default:   return option.label;
    }
};

const initialValueFor = (v) => {
    if (v.kind === 'date') {
        const def = (v.repdefvalue || '').toLowerCase();
        if (def === 'now()' || def === 'now') return dayjs();
        if (def) {
            const parsed = dayjs(def);
            return parsed.isValid() ? parsed : null;
        }
        return null;
    }
    if (v.kind === 'number') {
        return v.repdefvalue ?? '';
    }
    if (v.kind === 'multi') {
        return [];
    }
    return v.repdefvalue ?? '';
};

const serialiseForBackend = (v, raw) => {
    if (v.kind === 'date') {
        if (!raw) return '';
        if (raw && typeof raw === 'object' && raw.format) {
            return raw.format('YYYY-MM-DD');
        }
        return String(raw);
    }
    if (v.kind === 'multi') {
        if (!Array.isArray(raw)) return [];
        return raw.map(o => (typeof o === 'object' && o !== null ? o.value : o));
    }
    return raw ?? '';
};

const optionsForVariable = (v, options) => {
    if (!options || !v.option_source) return [];
    if (v.option_source === 'codes' || v.option_source === 'kl_codes') {
        const bucket = options[v.option_source] || {};
        return bucket[v.option_group] || [];
    }
    return options[v.option_source] || [];
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const ReportRunModal = (props) => {
    const { open, report, onClose } = props;

    // 'vars' | 'running' | 'result' | 'error'
    const [phase, setPhase]               = useState('vars');
    const [loadingVars, setLoadingVars]   = useState(false);
    const [variables, setVariables]       = useState([]);   // server-side definitions
    const [optionLists, setOptionLists]   = useState({});
    const [values, setValues]             = useState({});   // userName → raw value

    const [runId, setRunId]               = useState(null);
    const [statusPayload, setStatusPayload] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    const pollTimer = useRef(null);
    const pollAttempts = useRef(0);

    // Keep latest props in a ref so the polling chain (scheduled via setTimeout +
    // useCallback) doesn't capture stale values from the first render.
    const apiRef       = useRef(props.api);
    const tRef         = useRef(props.t);
    const sftUserIdRef = useRef(props.sftUserId);
    useEffect(() => { apiRef.current       = props.api;       }, [props.api]);
    useEffect(() => { tRef.current         = props.t;         }, [props.t]);
    useEffect(() => { sftUserIdRef.current = props.sftUserId; }, [props.sftUserId]);

    // Reset whenever modal closes
    useEffect(() => {
        if (!open) {
            setPhase('vars');
            setLoadingVars(false);
            setVariables([]);
            setOptionLists({});
            setValues({});
            setRunId(null);
            setStatusPayload(null);
            setErrorMessage(null);
            pollAttempts.current = 0;
            if (pollTimer.current) {
                clearTimeout(pollTimer.current);
                pollTimer.current = null;
            }
        }
    }, [open]);

    // Load variables when the modal opens (if the report has any)
    useEffect(() => {
        if (!open || !report) return;

        if (!report.has_variables) {
            // Skip straight to execution.
            startExecution({});
            return;
        }

        setLoadingVars(true);
        SftGlobalData.post('reports&action=fetchReportVariables', {
            key: props.api,
            repid: report.repid,
        }).then(response => {
            if (typeof response.error === 'undefined' && response?.data) {
                const vars = response.data.variables || [];
                setVariables(vars);
                setOptionLists(response.data.options || {});
                const initial = {};
                vars.forEach(v => { initial[v.repvar] = initialValueFor(v); });
                setValues(initial);
            } else {
                setErrorMessage((response && response.message) || (props.t('errorOccured') || 'Error'));
                setPhase('error');
            }
            setLoadingVars(false);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, report?.repid]);

    // ─────────────────────────────────────────────────────────────────────────
    // Execution: start + poll
    // ─────────────────────────────────────────────────────────────────────────

    const startExecution = (varValuesOverride) => {
        const rawValues = varValuesOverride ?? values;
        const payload = {};
        variables.forEach(v => {
            payload[v.repvar] = serialiseForBackend(v, rawValues[v.repvar]);
        });

        setPhase('running');
        setErrorMessage(null);
        setStatusPayload(null);
        pollAttempts.current = 0;

        SftGlobalData.post('reports&action=execute', {
            key: props.api,
            sft_user_id: sftUserIdRef.current || props.sftUserId || '',
            repid: report.repid,
            variables: payload,
        }).then(response => {
            if (typeof response.error === 'undefined' && response?.data?.run_id) {
                setRunId(response.data.run_id);
                schedulePoll(response.data.run_id, 700);
            } else {
                setErrorMessage((response && response.message) || (props.t('errorOccured') || 'Error'));
                setPhase('error');
            }
        });
    };

    const schedulePoll = useCallback((id, delayMs) => {
        if (pollTimer.current) clearTimeout(pollTimer.current);
        pollTimer.current = setTimeout(() => pollStatus(id), delayMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pollStatus = (id) => {
        SftGlobalData.post('reports&action=status', {
            key: apiRef.current,
            run_id: id,
        }).then(response => {
            const tx = tRef.current || ((k) => k);
            if (typeof response.error !== 'undefined' || !response?.data) {
                setErrorMessage((response && response.message) || tx('reports_error') || 'Error');
                setPhase('error');
                return;
            }
            const data = response.data;
            setStatusPayload(data);

            if (data.status === 'done') {
                setPhase('result');
                return;
            }
            if (data.status === 'error') {
                setErrorMessage(data.error || tx('reports_error') || 'Error');
                setPhase('error');
                return;
            }
            if (data.status === 'cancelled') {
                setPhase('vars');
                return;
            }

            // Still queued / running — back off after a while.
            pollAttempts.current += 1;
            const delay = pollAttempts.current < 10 ? 1000
                        : pollAttempts.current < 30 ? 2000
                        : 5000;
            schedulePoll(id, delay);
        });
    };

    const cancelRun = () => {
        if (!runId) {
            setPhase('vars');
            return;
        }
        if (pollTimer.current) {
            clearTimeout(pollTimer.current);
            pollTimer.current = null;
        }
        SftGlobalData.post('reports&action=cancel', {
            key: apiRef.current,
            run_id: runId,
        }).finally(() => {
            setPhase('vars');
            setRunId(null);
        });
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Validation
    // ─────────────────────────────────────────────────────────────────────────

    const canSubmit = useMemo(() => {
        for (const v of variables) {
            if (!v.rep_must_use) continue;
            const raw = values[v.repvar];
            if (v.kind === 'multi') {
                if (!Array.isArray(raw) || raw.length === 0) return false;
            } else if (v.kind === 'date') {
                if (!raw) return false;
            } else {
                if (raw === undefined || raw === null || String(raw).trim() === '') return false;
            }
        }
        return true;
    }, [variables, values]);

    // ─────────────────────────────────────────────────────────────────────────
    // Render — variable input
    // ─────────────────────────────────────────────────────────────────────────

    const renderVariableInput = (v) => {
        const label = localizedVarLabel(v, props.lang) + (v.rep_must_use ? ' *' : '');
        const value = values[v.repvar];

        if (v.kind === 'date') {
            return (
                <DatePicker
                    key={v.repvar}
                    label={label}
                    value={value || null}
                    onChange={(newVal) => setValues(s => ({ ...s, [v.repvar]: newVal }))}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
            );
        }
        if (v.kind === 'number') {
            return (
                <TextField
                    key={v.repvar}
                    label={label}
                    type="number"
                    size="small"
                    fullWidth
                    value={value ?? ''}
                    onChange={(e) => setValues(s => ({ ...s, [v.repvar]: e.target.value }))}
                />
            );
        }
        if (v.kind === 'multi') {
            const options = optionsForVariable(v, optionLists);
            const isCodeSource = v.option_source === 'codes' || v.option_source === 'kl_codes';
            const labelFn = isCodeSource
                ? (opt) => `${opt.value} — ${localizedCodeLabel(opt, props.lang)}`
                : (opt) => `${opt.value} — ${opt.label || ''}`;
            return (
                <Autocomplete
                    key={v.repvar}
                    multiple
                    size="small"
                    options={options}
                    value={Array.isArray(value) ? value : []}
                    onChange={(_, newVal) => setValues(s => ({ ...s, [v.repvar]: newVal }))}
                    isOptionEqualToValue={(opt, val) => opt.value === val.value}
                    getOptionLabel={labelFn}
                    renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                            <Chip
                                size="small"
                                label={option.value}
                                {...getTagProps({ index })}
                                key={option.value}
                            />
                        ))
                    }
                    renderInput={(params) => <TextField {...params} label={label} placeholder={props.t('reports_select') || 'Select…'} />}
                    sx={{ width: '100%' }}
                />
            );
        }
        // text / single
        return (
            <TextField
                key={v.repvar}
                label={label}
                size="small"
                fullWidth
                value={value ?? ''}
                onChange={(e) => setValues(s => ({ ...s, [v.repvar]: e.target.value }))}
            />
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Render shells
    // ─────────────────────────────────────────────────────────────────────────

    const title = localizedTitle(report, props.lang);
    const isNarrow = (props.screenWidth ?? 1200) < 700;

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (reason === 'backdropClick' && phase === 'running') return;
                onClose && onClose();
            }}
            fullWidth
            maxWidth={phase === 'result' ? false : 'md'}
            PaperProps={{
                sx: {
                    m: isNarrow ? 1 : 4,
                    width: isNarrow ? 'calc(100% - 16px)' : 'calc(100% - 64px)',
                    ...(phase === 'result' ? { maxWidth: isNarrow ? 'calc(100% - 16px)' : 'calc(100% - 64px)' } : {}),
                    maxHeight: isNarrow ? 'calc(100% - 16px)' : 'calc(100% - 64px)',
                    height: phase === 'result' ? (isNarrow ? 'calc(100% - 16px)' : 'calc(100% - 64px)') : undefined,
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
                <span>{title}</span>
                <IconButton size="small" onClick={onClose} disabled={phase === 'running'}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* ─── Variable input phase ─── */}
                {phase === 'vars' && (
                    <>
                        {loadingVars && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress />
                            </Box>
                        )}
                        {!loadingVars && variables.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                {props.t('reports_noVariables') || 'This report has no input variables. Click Run to execute.'}
                            </Typography>
                        )}
                        {!loadingVars && variables.length > 0 && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                {variables.map(renderVariableInput)}
                            </Box>
                        )}
                    </>
                )}

                {/* ─── Running phase ─── */}
                {phase === 'running' && (
                    <>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <CircularProgress size={22} />
                            <Typography variant="body2">
                                {props.t('reports_running') || 'Running report…'}
                                {statusPayload && statusPayload.step && statusPayload.step_count
                                    ? ` (${statusPayload.step}/${statusPayload.step_count})`
                                    : ''}
                            </Typography>
                        </Stack>
                        <LinearProgress />
                        {[0, 1, 2, 3, 4, 5].map(i => (
                            <Skeleton key={i} variant="text" height={28} />
                        ))}
                    </>
                )}

                {/* ─── Result phase ─── */}
                {phase === 'result' && statusPayload && (
                    isSizeMatrix(statusPayload.columns || [])
                        ? (
                            <SizeMatrixReportGrid
                                columns={statusPayload.columns || []}
                                rows={statusPayload.rows || []}
                                truncated={!!statusPayload.truncated}
                                rowCount={statusPayload.row_count || 0}
                                layout={statusPayload.layout || null}
                                showSubtotals={/tussentota|subtota|sous-?tota/i.test(
                                    [report?.reptitle, report?.reptitle2, report?.reptitle3, report?.reptitle4].filter(Boolean).join(' ')
                                )}
                                t={props.t}
                                lang={props.lang}
                                theme={props.theme}
                                reportTitle={title}
                            />
                        )
                        : (
                            <ReportResultGrid
                                columns={statusPayload.columns || []}
                                rows={statusPayload.rows || []}
                                truncated={!!statusPayload.truncated}
                                rowCount={statusPayload.row_count || 0}
                                layout={statusPayload.layout || null}
                                t={props.t}
                                lang={props.lang}
                                theme={props.theme}
                                reportTitle={title}
                            />
                        )
                )}

                {/* ─── Error phase ─── */}
                {phase === 'error' && (
                    <Alert severity="error">
                        {errorMessage || (props.t('reports_error') || 'Error')}
                    </Alert>
                )}
            </DialogContent>

            <DialogActions>
                {phase === 'vars' && (
                    <>
                        <Button onClick={onClose}>{props.t('reports_close') || 'Close'}</Button>
                        <Button
                            variant="contained"
                            startIcon={<PlayArrowIcon />}
                            disabled={!canSubmit || loadingVars}
                            onClick={() => startExecution(null)}
                        >
                            {props.t('reports_run') || 'Run'}
                        </Button>
                    </>
                )}
                {phase === 'running' && (
                    <Button color="warning" onClick={cancelRun}>
                        {props.t('reports_cancel') || 'Cancel'}
                    </Button>
                )}
                {phase === 'result' && (
                    <>
                        <Button onClick={() => { setPhase('vars'); setStatusPayload(null); setRunId(null); }}>
                            {props.t('reports_runAgain') || 'Run again'}
                        </Button>
                        <Button variant="contained" onClick={onClose}>
                            {props.t('reports_close') || 'Close'}
                        </Button>
                    </>
                )}
                {phase === 'error' && (
                    <>
                        <Button onClick={() => setPhase('vars')}>
                            {props.t('reports_back') || 'Back'}
                        </Button>
                        <Button variant="contained" onClick={onClose}>
                            {props.t('reports_close') || 'Close'}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ReportRunModal;
